/**
 * ── THE MAP'S `Group by:` CONTROL — pins or countries (VERA-260) ────────────────────
 *
 * What ONE MARK IS on this map: a located EVENT, or a COUNTRY. It is the same axis the
 * leaderboard's `Group by:` already exposes over its rows, it carries the same label, and
 * it uses the same `ViewToggle` treatment (§28 — reuse before hand-rolling), so the two
 * read as one idea applied to two surfaces rather than as two unrelated controls that
 * happen to switch something.
 *
 * ## ⚠️ HOW THIS RELATES TO THE EXISTING `Pins | Heat` CONTROL — decided, not defaulted
 *
 * The map already had a render-mode toggle offering `Pins` and `Heat`, and the naive move
 * was to grow it into `Pins | Heat | Countries`. That is wrong, and the reason is that the
 * three are not siblings:
 *
 *   grouping    what a mark IS        pin (an event)  |  country (an area)
 *   render      how those marks DRAW  pins            |  heat surface
 *
 * `Heat` is a RENDER OF THE PIN GROUPING — a density surface over the same located events
 * the pins plot. The choropleth is not a third render of that; it is the only render the
 * COUNTRY grouping has. So a flat three-way control would put a parent and one of its own
 * children on the same footing, and the day a second country render existed (a bubble
 * layer, say) the list would have no way to say which grouping it belonged to.
 *
 * THE RELATIONSHIP IS THEREFORE HIERARCHICAL: this control is the outer axis; the render
 * toggle is scoped INSIDE the pin grouping.
 *
 * ⚠️ AND THEY ARE DELIBERATELY NOT ADJACENT. This one lives in the card's HEADER (it
 * decides which component the card body is); the render toggle stays on the map CANVAS,
 * where it already renders, because it modifies the surface it sits on. That separation
 * does a second job for free: the two controls would otherwise both show an option
 * labelled `Pins`, 16px apart, meaning two different things — "a mark is one scan" and
 * "draw those scans as discrete dots". Putting the parent in the chrome and the child on
 * the thing it modifies is what makes the repeated noun read as a hierarchy instead of as
 * a duplicate.
 *
 * The render toggle needs no hide rule: at `country` grouping the card renders
 * `ScansByCountry` instead of `ScansByLocation`, so the control is not merely disabled but
 * genuinely absent along with the surface it belongs to. §46's obligation on a hidden
 * state-block — the RESOLVING action must be reachable in that state — is discharged by
 * this control, which is on screen throughout.
 *
 * ## Refusals are DISABLED, never hidden (§46)
 *
 * A grouping this metric cannot take stays visible and dimmed, with the capability
 * sentence as its tooltip. That is §46's permission-shaped treatment applied to a
 * data-shaped block, and it is the right call here for the reason §46 gives for
 * `ViewToggle.disabled` itself: hiding teaches nothing. A reader looking at Certificates
 * on a map needs to know that a country grouping EXISTS and why this metric is not offered
 * it — "the two would disagree, so it is not offered until the per-country figure is
 * grouped on each item's latest scan" is a fact about our build state that a reader should
 * expect to change, and an absent option cannot say it.
 *
 * The sentences are `COUNTRY_GROUPABLE`'s and `analyticsMapPinCapability`'s, verbatim.
 * Never a fresh one written here: they are already distinguished between durable
 * (data-shaped, permanent) and build-state (ours to close), and re-wording them at the
 * control is how the two classes come to sound alike.
 */
import React, { useMemo } from 'react'

import { Stack, Typography } from '@mui/material'

import { InfoTooltip } from 'components/feedback/InfoTooltip'
import { ViewToggle, type ViewToggleOption } from 'components/input/ViewToggle/ViewToggle'

import {
  ANALYTICS_MAP_GROUPING_CAVEAT,
  analyticsMapGroupingCapability,
  type AnalyticsMapGrouping,
} from './analyticsDisplay'

/**
 * WORDS, not icons — the same call `ScansByLocationModeToggle` and
 * `AnalyticsDimensionToggle` both make, and for the reason `ViewToggle`'s own two-label
 * note gives: a glyph is only lighter when it is CONVENTIONAL. `MapPinIcon` would read
 * for pins; there is no established glyph for "grouped by country" that a reader decodes
 * without a legend, and a half-iconified pair is worse than either.
 */
const GROUPING_LABELS: Record<AnalyticsMapGrouping, string> = {
  pin: 'Pins',
  country: 'Countries',
}

const GROUPING_ORDER: readonly AnalyticsMapGrouping[] = ['pin', 'country']

interface AnalyticsMapGroupingToggleProps {
  /**
   * The METRIC card's id — not the map variant's.
   *
   * Capability is a property of the measurement, not of the card that draws it, and
   * `COUNTRY_GROUPABLE` is keyed on the metric. Passing the variant id would make the
   * lookup depend on a grammar this component has no reason to know.
   */
  metricCardId: string
  value: AnalyticsMapGrouping
  onChange: (next: AnalyticsMapGrouping) => void
}

export const AnalyticsMapGroupingToggle = ({
  metricCardId,
  value,
  onChange,
}: AnalyticsMapGroupingToggleProps) => {
  const options = useMemo<readonly ViewToggleOption<AnalyticsMapGrouping>[]>(
    () =>
      GROUPING_ORDER.map(grouping => {
        const capability = analyticsMapGroupingCapability(metricCardId, grouping)
        return {
          value: grouping,
          label: GROUPING_LABELS[grouping],
          disabled: !capability.capable,
          // §46's forcing function, and `ViewToggle` requires the pairing: a disabled
          // option MUST carry the sentence saying why. There is always one here — a
          // capability that refuses always carries a reason by construction.
          disabledReason: capability.capable ? undefined : capability.reason,
        }
      }),
    [metricCardId],
  )

  /**
   * THE CAVEAT IS ON THE LABEL, not on the `Countries` option.
   *
   * It is a fact about the RELATIONSHIP between the two groupings — a country can count
   * more than the pins inside it — so it belongs to the control that switches between
   * them, where a reader meets it BEFORE they toggle and see the number move. Hung off the
   * `Countries` option it would be a tooltip nobody opens (the option is enabled, so there
   * is no reason to hover it) and it would arrive after the surprise rather than before.
   *
   * Rendered whenever the control is, including on a metric that can only be one of the
   * two: the sentence explains what the axis MEANS, and a reader looking at a
   * country-only rate still benefits from knowing what a fill counts.
   */
  return (
    <Stack direction="row" alignItems="center" spacing={2}>
      <Typography variant="body3" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
        Group by:
      </Typography>
      <InfoTooltip text={ANALYTICS_MAP_GROUPING_CAVEAT} />
      <ViewToggle
        options={options}
        value={value}
        onChange={onChange}
        ariaLabel="What one mark on this map counts"
      />
    </Stack>
  )
}
